/**
 * @fileoverview Cinematic Post-Processing Engine v10.2.0 — WebGPU Native & AI-Powered
 * @module MikhmelViewer/CinematicRendering
 *
 * Конвейер v10 (WebGPU):
 *   CS3D canvas → Capture → Velocity → SSAO → AO v2 (denoise+temporal) → AO Apply
 *   → SSGI → SSGI Resolve → SSS → Volumetric Light → Bright → Bloom → GodRays
 *   → Composite → SSR → Fog → LUT → Bokeh DOF → Motion Blur → AI Denoise
 *   → FSR Upscale (EASU+RCAS) → FXAA → Present
 *
 * Что изменилось относительно v9.1.0:
 *   • WebGPU вместо WebGL2, весь шейдерный стек переписан на WGSL
 *   • compute-шейдеры: денойзинг, апскейл, редукция статистики, объёмы
 *   • новые эффекты: volumetric lighting (raymarching), SSGI, motion blur,
 *     AO v2 с темпоральной репроекцией, subsurface scattering
 *   • AI-слой: автоподбор пресетов, денойзинг, FSR 1.0
 *   • NeRF-инференс и прямой объёмный рендеринг медицинских данных
 *
 * СОВМЕСТИМОСТЬ. Публичный API v9.1.0 сохранён полностью: те же экспорты,
 * те же имена методов, тот же формат Result ({success, value} / {success, error}).
 * Существующий код продолжит работать без правок. Единственная содержательная
 * разница — инициализация GPU стала асинхронной: enable() по-прежнему
 * возвращает синхронный результат, но фактическая готовность наступает
 * позже, о чём сообщает событие CINEMATIC_ENABLED и промис whenReady().
 *
 * ОТКАТ. Если WebGPU недоступен (Firefox без флага, Safari < 18, старые
 * драйверы), рендерер прозрачно передаёт управление реализации v9.1.0 на
 * WebGL2. Путь к ней задаётся options.legacyModulePath.
 */

import { get, set, KEYS } from '../core/storage.js';
import { events } from '../core/events.js';
import { EVENTS } from '../core/eventBus.js';
import { log } from '../utils/logger.js';
import { ОсьКадра, статистикаПоБайтам } from './frameMeasure.js';

import { WebGPUBackend, FORMATS, setBackendLogger } from './cinematic10/webgpuBackend.js';
import { AIDenoiser, Upscaler, setAILogger, DENOISE_PRESETS, UPSCALE_QUALITY } from './cinematic10/aiRendering.js';
import { SceneAnalyzer, AIPresetOptimizer, setPresetLogger } from './cinematic10/aiPresets.js';
import { NeRFRenderer, VolumeRenderer, setVolumeLogger, mat4, VOLUME_WINDOW_PRESETS } from './cinematic10/nerfRenderer.js';
import { setOrtLogger, isOrtLoaded, getOrtBackend } from './cinematic10/ortLoader.js';
import {
  CINEMATIC_PRESETS as _PRESETS,
  LUT_MODES, TONE_MAP_MODES,
} from './cinematic10/presets.js';

// ═══════════════════════════════════════════════════════════════════
// 1. RESULT PATTERN
// ═══════════════════════════════════════════════════════════════════

const Ok = (value) => ({ success: true, value });
const Err = (error) => ({ success: false, error });

setBackendLogger(log);
setAILogger(log);
setPresetLogger(log);
setVolumeLogger(log);
setOrtLogger(log);

export const CINEMATIC_PRESETS = _PRESETS;

// ═══════════════════════════════════════════════════════════════════
// 2. УТИЛИТЫ
// ═══════════════════════════════════════════════════════════════════

const _deepClone = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (typeof structuredClone === 'function') {
    try { return structuredClone(obj); } catch (e) { log.warn('[Cinematic] structuredClone failed:', e.message); }
  }
  if (obj instanceof Date) return new Date(obj.getTime());
  if (Array.isArray(obj)) return obj.map(item => _deepClone(item));
  const cloned = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) cloned[key] = _deepClone(obj[key]);
  }
  return cloned;
};

const _validatePreset = (preset) => {
  if (!preset || typeof preset !== 'object') return false;
  if (!preset.name || typeof preset.name !== 'string') return false;
  if (preset.ssao && typeof preset.ssao !== 'object') return false;
  if (preset.bloom && typeof preset.bloom !== 'object') return false;
  return true;
};

const _deepMerge = (target, source) => {
  const result = _deepClone(target);
  for (const key of Object.keys(source)) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      if (!result[key]) result[key] = {};
      result[key] = _deepMerge(result[key], source[key]);
    } else {
      result[key] = source[key];
    }
  }
  return result;
};

// ═══════════════════════════════════════════════════════════════════
// 3. PERFORMANCE MONITOR (DRS + адаптивное качество)
// ═══════════════════════════════════════════════════════════════════

class PerformanceMonitor {
  constructor(options = {}) {
    this._targetFPS = options.targetFPS ?? 60;
    this._minScale = options.minScale ?? 0.5;
    this._maxScale = options.maxScale ?? 1.0;
    this._scaleStep = options.scaleStep ?? 0.1;
    this._stabilityFrames = options.stabilityFrames ?? 30;
    this._degradeThreshold = options.degradeThreshold ?? 0.85;
    this._upgradeThreshold = options.upgradeThreshold ?? 0.95;
    this._frameTimes = [];
    this._currentScale = 1.0;
    this._stableCount = 0;
    this._lastDegradeTime = 0;
    this._degradeCooldown = 2000;
    this._metrics = {
      avgFPS: 0, minFPS: 60, maxFPS: 60,
      frameTime: 0, renderTime: 0, scale: 1.0,
      totalFrames: 0, droppedFrames: 0,
      gpuPasses: 0, gpuDispatches: 0,
    };
  }

  recordFrame(renderTimeMs, gpuStats = null) {
    const now = performance.now();
    this._frameTimes.push({ time: now, renderTime: renderTimeMs });
    this._metrics.totalFrames++;
    if (this._frameTimes.length > 60) this._frameTimes.shift();
    if (gpuStats) {
      this._metrics.gpuPasses = gpuStats.passes;
      this._metrics.gpuDispatches = gpuStats.dispatches;
    }
    this._updateMetrics();
    return this._evaluateScale(now);
  }

  _updateMetrics() {
    if (this._frameTimes.length < 2) return;
    const times = this._frameTimes;
    const durations = [];
    for (let i = 1; i < times.length; i++) durations.push(times[i].time - times[i - 1].time);

    const avgFrame = durations.reduce((a, b) => a + b, 0) / durations.length;
    const minFrame = Math.min(...durations);
    const maxFrame = Math.max(...durations);
    const avgRender = times.reduce((a, b) => a + b.renderTime, 0) / times.length;

    this._metrics.avgFPS = Math.round(1000 / avgFrame);
    this._metrics.minFPS = Math.round(1000 / maxFrame);
    this._metrics.maxFPS = Math.round(1000 / minFrame);
    this._metrics.frameTime = avgFrame;
    this._metrics.renderTime = avgRender;
    this._metrics.scale = this._currentScale;
    if (avgFrame > 1000 / (this._targetFPS * 0.5)) this._metrics.droppedFrames++;
  }

  _evaluateScale(now) {
    if (this._frameTimes.length < this._stabilityFrames) {
      return { scaleChanged: false, newScale: this._currentScale };
    }
    const fpsRatio = this._metrics.avgFPS / this._targetFPS;

    if (fpsRatio < this._degradeThreshold &&
        this._currentScale > this._minScale &&
        now - this._lastDegradeTime > this._degradeCooldown) {
      const newScale = Math.max(this._minScale, this._currentScale - this._scaleStep);
      this._currentScale = newScale;
      this._lastDegradeTime = now;
      this._stableCount = 0;
      log.warn(`[Cinematic/DRS] Снижение: масштаб ${newScale.toFixed(2)} (FPS: ${this._metrics.avgFPS})`);
      return { scaleChanged: true, newScale, reason: 'fps_drop' };
    }

    if (fpsRatio > this._upgradeThreshold && this._currentScale < this._maxScale) {
      this._stableCount++;
      if (this._stableCount >= this._stabilityFrames) {
        const newScale = Math.min(this._maxScale, this._currentScale + this._scaleStep);
        if (newScale !== this._currentScale) {
          this._currentScale = newScale;
          this._stableCount = 0;
          log.info(`[Cinematic/DRS] Повышение: масштаб ${newScale.toFixed(2)} (FPS: ${this._metrics.avgFPS})`);
          return { scaleChanged: true, newScale, reason: 'fps_stable' };
        }
      }
    } else {
      this._stableCount = 0;
    }
    return { scaleChanged: false, newScale: this._currentScale };
  }

  /** Бюджеты качества по фактическому FPS. Расширены под новые проходы v10. */
  getAdaptiveQuality() {
    const fps = this._metrics.avgFPS;
    if (fps < 30) {
      return { ssaoSamples: 8, bloomPasses: 2, godRaysSamples: 15, temporalWeight: 0.95,
               volumetricSteps: 16, ssgiSamples: 8, motionBlurSamples: 6, bokehRings: 2,
               ssrSteps: 6, sssSamples: 6, denoiseRadius: 1 };
    }
    if (fps < 45) {
      return { ssaoSamples: 16, bloomPasses: 3, godRaysSamples: 20, temporalWeight: 0.9,
               volumetricSteps: 24, ssgiSamples: 12, motionBlurSamples: 8, bokehRings: 3,
               ssrSteps: 8, sssSamples: 8, denoiseRadius: 1 };
    }
    if (fps < 55) {
      return { ssaoSamples: 24, bloomPasses: 4, godRaysSamples: 25, temporalWeight: 0.85,
               volumetricSteps: 40, ssgiSamples: 16, motionBlurSamples: 12, bokehRings: 4,
               ssrSteps: 12, sssSamples: 12, denoiseRadius: 2 };
    }
    return { ssaoSamples: 32, bloomPasses: 5, godRaysSamples: 30, temporalWeight: 0.8,
             volumetricSteps: 64, ssgiSamples: 24, motionBlurSamples: 16, bokehRings: 5,
             ssrSteps: 16, sssSamples: 16, denoiseRadius: 2 };
  }

  getMetrics() { return { ...this._metrics }; }
  getScale() { return this._currentScale; }

  setTargetFPS(fps) {
    if (typeof fps === 'number' && fps > 0 && fps <= 240) {
      this._targetFPS = fps;
      return Ok(true);
    }
    return Err('Invalid FPS value');
  }

  reset() {
    this._frameTimes = [];
    this._currentScale = 1.0;
    this._stableCount = 0;
    this._metrics.totalFrames = 0;
    this._metrics.droppedFrames = 0;
  }
}

// ═══════════════════════════════════════════════════════════════════
// 4. PARAMS HISTORY (Undo/Redo)
// ═══════════════════════════════════════════════════════════════════

class ParamsHistory {
  constructor(maxSize = 50) {
    this._stack = [];
    this._redoStack = [];
    this._maxSize = maxSize;
  }

  push(state, label = 'change') {
    this._stack.push({ state: _deepClone(state), label, timestamp: Date.now() });
    if (this._stack.length > this._maxSize) this._stack.shift();
    this._redoStack = [];
    return this.size();
  }

  undo(currentState) {
    if (this._stack.length === 0) return Err('Nothing to undo');
    const entry = this._stack.pop();
    this._redoStack.push({ state: _deepClone(currentState), label: 'undo_target', timestamp: Date.now() });
    return Ok({ state: entry.state, label: entry.label });
  }

  redo(currentState) {
    if (this._redoStack.length === 0) return Err('Nothing to redo');
    const entry = this._redoStack.pop();
    this._stack.push({ state: _deepClone(currentState), label: 'redo_target', timestamp: Date.now() });
    return Ok({ state: entry.state, label: entry.label });
  }

  size() { return { undo: this._stack.length, redo: this._redoStack.length }; }
  clear() { this._stack = []; this._redoStack = []; }
}

// ═══════════════════════════════════════════════════════════════════
// 5. CINEMATIC RENDERER v10
// ═══════════════════════════════════════════════════════════════════

export class CinematicRenderer {
  constructor(viewport, options = {}) {
    if (!viewport) throw new Error('[Cinematic] Viewport is required');

    this._viewport = viewport;
    this._enabled = false;
    this._disposed = false;
    this._isContextLost = false;
    this._animFrame = null;
    // [10.3.0] Версия — от сборщика, а не строкой в коде: иначе её надо помнить
    // и править при каждом выпуске, а забудешь — краснеет тест сравнения с
    // package.json на ровном месте.
    this._version = (typeof __APP_VERSION__ !== 'undefined') ? __APP_VERSION__ : '0.0.0';

    const initialPreset = options.preset || 'cinematic';
    if (!CINEMATIC_PRESETS[initialPreset]) {
      log.warn(`[Cinematic] Неизвестный пресет "${initialPreset}", берём "cinematic"`);
    }
    this._preset = CINEMATIC_PRESETS[initialPreset] ? initialPreset : 'cinematic';
    this._params = _deepClone(CINEMATIC_PRESETS[this._preset]);
    this._customParams = {};
    this._splitPos = 0;
    this._startTime = performance.now();

    // ── GPU-состояние ──
    this._backend = null;
    this._canvas = null;
    this._gpuReady = false;
    this._gpuInitPromise = null;
    this._readyResolvers = [];

    // ── Откат на WebGL2 ──
    this._legacyRenderer = null;
    this._usingLegacy = false;
    // null = штатный путь через статический import() (см. _loadLegacyModule).
    // Строка здесь — только для нестандартного размещения legacy-модуля.
    this._legacyModulePath = options.legacyModulePath || null;
    this._allowLegacyFallback = options.allowLegacyFallback !== false;

    // ── Источники данных ──
    this._depthSrc = null;
    this._hasDepth = false;
    this._aiMaskColor = [1, 0, 0];
    this._aiMaskOpacity = 0;
    this._hasAIMask = false;

    // ── Камера (нужна для velocity, volumetric, объёмного рендера) ──
    this._viewProj = null;
    this._prevViewProj = null;
    this._cameraPos = [0, 0, 5];
    this._hasMatrices = false;

    // ── Источники света для объёмного освещения ──
    this._lights = [
      { position: [0, 3, 3], intensity: 1.0, color: [1, 0.96, 0.9], radius: 12 },
    ];

    // ── Темпоральное состояние ──
    this._enableTemporalSSAO = true;
    this._historyValid = false;

    // ── Цикл рендера ──
    this._needsRender = true;
    this._isRendering = false;
    /** [10.19.19] Причина, по которой кадра нет. Пусто — кадры идут. */
    this._простой = { причина: '', кадров: 0, ошибка: '' };
    this._renderQueue = [];
    this._isInteracting = false;
    this._isDraggingSplit = false;
    this._frameCounter = 0;

    // ── Производительность ──
    this._perfMonitor = new PerformanceMonitor({
      targetFPS: options.targetFPS ?? 60,
      minScale: 0.5,
      maxScale: 1.0,
    });
    this._useDRS = options.useDRS !== false;
    // [10.19.12] Разрешение рендера, заданное человеком: длинная сторона в
    // пикселях (256 / 512 / 1024 / 2048 из панели «Качество»). 0 = «по размеру
    // окна», как было раньше.
    //
    // ПОВОД. До 10.19.12 выбор качества до рендерера НЕ ДОХОДИЛ: значение
    // ползунка читалось в featureBindings и выбрасывалось, а разрешение всегда
    // считалось от размера канвы Cornerstone. Врач переключал «Превью 256px» и
    // «Ультра 2048px» — не менялось ничего, включая время рендера.
    this._resolutionCap = Number(options.resolutionCap) > 0 ? Math.floor(options.resolutionCap) : 0;
    this._targetW = 0;
    this._targetH = 0;
    this._renderW = 0;
    this._renderH = 0;

    this._history = new ParamsHistory(options.historySize ?? 50);
    this._history.push(this._params, 'initial');

    // ── AI-модули (создаются после инициализации бэкенда) ──
    this._denoiser = null;
    this._upscaler = null;
    this._sceneAnalyzer = null;
    this._presetOptimizer = null;
    this._nerf = null;
    this._volume = null;
    this._autoPresetEnabled = options.autoPreset === true;

    this._mediaRecorder = null;
    this._recordedChunks = [];
    this._eventHandlers = new Map();
    this._resizeObserver = null;
    this._viewportCleanupHandler = null;

    this._storageKey = KEYS.CINEMATIC_PRESETS || 'mikhmel_cinematic_presets';
    this._options = options;

    if (options.autoStart) {
      const result = this.enable();
      if (!result.success) log.error('[Cinematic] Автозапуск не удался:', result.error);
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // ЖИЗНЕННЫЙ ЦИКЛ
  // ═══════════════════════════════════════════════════════════════

  /**
   * Включить рендерер. Возвращает синхронный Result (как в v9.1), но сама
   * инициализация GPU асинхронна — дождаться её можно через whenReady().
   */
  enable() {
    if (this._disposed) return Err('Renderer disposed');
    if (this._enabled) return Ok(true);

    try {
      this._enabled = true;
      this._needsRender = true;

      this._gpuInitPromise = this._initGPU()
        .then((result) => {
          if (!result.success) {
            log.warn('[Cinematic] Инициализация WebGPU не удалась:', result.error);
            return this._tryLegacyFallback(result.error);
          }
          this._bindEvents();
          this._startLoop();
          events.emit(EVENTS.CINEMATIC_ENABLED, {
            preset: this._preset, backend: 'webgpu', version: this._version,
          });
          log.info(`[Cinematic] Включён — WebGPU, пресет: ${this._preset}`);
          this._resolveReady(Ok('webgpu'));
          return Ok('webgpu');
        })
        .catch((e) => {
          log.error('[Cinematic] Ошибка при включении:', e);
          const r = Err(e.message);
          this._resolveReady(r);
          return r;
        });

      return Ok(true);
    } catch (e) {
      this._enabled = false;
      log.error('[Cinematic] Enable failed:', e);
      return Err(e.message);
    }
  }

  /** Промис готовности GPU. Разрешается Result-объектом. */
  whenReady() {
    if (this._gpuReady) return Promise.resolve(Ok(this._usingLegacy ? 'webgl2' : 'webgpu'));
    if (!this._gpuInitPromise) return Promise.resolve(Err('Renderer not enabled'));
    return new Promise((resolve) => { this._readyResolvers.push(resolve); });
  }

  _resolveReady(result) {
    this._gpuReady = result.success;
    const resolvers = this._readyResolvers.splice(0);
    resolvers.forEach(r => { try { r(result); } catch { /* потребитель отвалился */ } });
  }

  disable() {
    if (!this._enabled) return Ok(true);
    this._enabled = false;
    if (this._animFrame) { cancelAnimationFrame(this._animFrame); this._animFrame = null; }
    if (this._usingLegacy && this._legacyRenderer) return this._legacyRenderer.disable();
    if (this._canvas) this._canvas.style.display = 'none';
    events.emit(EVENTS.CINEMATIC_DISABLED, { preset: this._preset });
    log.info('[Cinematic] Выключен');
    return Ok(true);
  }

  dispose() {
    if (this._disposed) return Ok(true);
    this.disable();
    this._disposed = true;

    if (this._viewportCleanupHandler && this._viewport?.element) {
      this._viewport.element.removeEventListener('destroy', this._viewportCleanupHandler);
      this._viewportCleanupHandler = null;
    }

    if (this._mediaRecorder) {
      try { this._mediaRecorder.stop(); } catch (e) { log.warn('[Cinematic] MediaRecorder stop failed:', e); }
      this._mediaRecorder = null;
    }

    this._unbindEvents();
    if (this._resizeObserver) { this._resizeObserver.disconnect(); this._resizeObserver = null; }

    this._denoiser?.dispose();
    this._upscaler?.dispose();
    this._sceneAnalyzer?.dispose();
    this._presetOptimizer?.dispose();
    this._nerf?.dispose();
    this._volume?.dispose();
    this._backend?.destroy();
    this._legacyRenderer?.dispose();

    this._backend = null;
    this._legacyRenderer = null;

    if (this._canvas?.parentNode) this._canvas.parentNode.removeChild(this._canvas);
    this._canvas = null;

    this._resolveReady(Err('disposed'));
    events.emit(EVENTS.CINEMATIC_DISPOSED, {});
    log.info('[Cinematic] Уничтожен');
    return Ok(true);
  }

  // ═══════════════════════════════════════════════════════════════
  // ИНИЦИАЛИЗАЦИЯ GPU
  // ═══════════════════════════════════════════════════════════════

  async _initGPU() {
    if (!WebGPUBackend.isSupported()) {
      return Err('WebGPU не поддерживается этим браузером');
    }

    const csCanvas = this._viewport.canvas || this._viewport.element?.querySelector('canvas');
    if (!csCanvas) return Err('Не найден canvas CS3D во вьюпорте');

    if (!this._viewportCleanupHandler) {
      this._viewportCleanupHandler = () => {
        if (!this._disposed) {
          log.warn('[Cinematic] Вьюпорт уничтожен, освобождаем рендерер');
          this.dispose();
        }
      };
      if (this._viewport.element) {
        this._viewport.element.addEventListener('destroy', this._viewportCleanupHandler);
      }
    }

    if (!this._canvas) {
      this._canvas = document.createElement('canvas');
      Object.assign(this._canvas.style, {
        position: 'absolute', top: '0', left: '0',
        width: '100%', height: '100%',
        pointerEvents: 'none', zIndex: '10',
      });
      const parent = csCanvas.parentElement;
      if (!parent) return Err('У canvas нет родительского элемента');
      if (getComputedStyle(parent).position === 'static') parent.style.position = 'relative';
      parent.appendChild(this._canvas);

      this._resizeObserver = new ResizeObserver(() => this._recalcResolution(csCanvas));
      this._resizeObserver.observe(parent);
    }

    this._backend = new WebGPUBackend();
    const initResult = await this._backend.init(this._canvas, {
      powerPreference: this._options.powerPreference || 'high-performance',
      alphaMode: 'premultiplied',
      onDeviceLost: (info) => this._handleDeviceLost(info),
    });
    if (!initResult.success) return initResult;

    // ── AI-модули ──
    this._denoiser = new AIDenoiser(this._backend, {
      preset: this._params.denoise?.preset || 'balanced',
      modelPath: this._options.denoiseModelPath || null,
    });
    this._upscaler = new Upscaler(this._backend, {
      quality: this._params.upscale?.quality || 'quality',
      sharpness: this._params.upscale?.sharpness ?? 0.25,
    });
    this._sceneAnalyzer = new SceneAnalyzer(this._backend, {
      intervalMs: this._options.analyzeIntervalMs ?? 500,
    });
    this._presetOptimizer = new AIPresetOptimizer({
      modelPath: this._options.presetModelPath,
      enabled: this._autoPresetEnabled,
    });
    this._nerf = new NeRFRenderer(this._backend, this._options.nerf || {});
    this._volume = new VolumeRenderer(this._backend, this._options.volume || {});

    if (this._options.presetModelPath) {
      // Отсутствие модели не критично — оптимизатор останется на эвристиках
      await this._presetOptimizer.loadModel();
    }

    this._recalcResolution(csCanvas);
    await this._warmup();

    return Ok(true);
  }

  async _warmup() {
    // Компиляция пайплайнов вперёд, чтобы первый кадр не встал колом
    const renderPasses = [
      'ssao', 'ssaoDenoise', 'aoApply', 'bright', 'blur', 'godRays',
      'composite', 'fxaa', 'blit', 'velocity',
    ];
    const computePasses = ['denoise', 'fsrEasu', 'fsrRcas'];

    const result = await this._backend.warmup(renderPasses, computePasses, FORMATS.HDR);
    if (!result.success) log.warn('[Cinematic] Прогрев частично не удался:', result.error);

    // Тяжёлые проходы компилируем в простое — они нужны не всем пресетам
    const deferred = ['volumetricLight', 'ssgi', 'ssgiResolve', 'motionBlur', 'sss',
                      'ssr', 'volFog', 'colorLut', 'bokehDof'];
    const compileDeferred = () => {
      if (this._disposed || !this._backend?.isReady()) return;
      this._backend.warmup(deferred, ['sceneStats', 'bilateralUpsample'], FORMATS.HDR)
        .then(() => log.debug('[Cinematic] Отложенные пайплайны скомпилированы'));
    };

    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(compileDeferred, { timeout: 3000 });
    } else {
      setTimeout(compileDeferred, 1000);
    }
  }

  async _handleDeviceLost(info) {
    if (this._disposed) return;
    this._isContextLost = true;
    this._gpuReady = false;
    if (this._animFrame) { cancelAnimationFrame(this._animFrame); this._animFrame = null; }

    events.emit(EVENTS.CINEMATIC_CONTEXT_LOST, { reason: info.reason });
    log.warn('[Cinematic] GPU-устройство потеряно, пробуем восстановить');

    // 'destroyed' означает намеренное уничтожение — восстанавливать нечего
    if (info.reason === 'destroyed') return;

    this._backend?.destroy();
    this._backend = null;
    this._historyValid = false;

    await new Promise(r => setTimeout(r, 500));
    if (this._disposed || !this._enabled) return;

    const result = await this._initGPU();
    if (result.success) {
      this._isContextLost = false;
      this._needsRender = true;
      this._startLoop();
      events.emit(EVENTS.CINEMATIC_CONTEXT_RESTORED, {});
      log.info('[Cinematic] GPU-устройство восстановлено');
    } else {
      log.error('[Cinematic] Восстановление не удалось:', result.error);
      await this._tryLegacyFallback(result.error);
    }
  }

  /**
   * Загрузка legacy-модуля v9.1.0.
   *
   * Путь обязан быть строковым литералом: только тогда Rollup/Vite видит
   * зависимость на этапе сборки и выпускает отдельный ленивый чанк. Импорт
   * по пути из переменной (даже с @vite-ignore) в бандл не попадает — в
   * продакшене рядом с собранным чанком никакого cinematicRenderingWebGL2.js
   * нет, и откат падает с 404 ровно там, где он единственный шанс показать
   * картинку: в браузере без WebGPU.
   *
   * Ветка с this._legacyModulePath оставлена для нестандартного размещения
   * модуля и работает только там, где файл реально лежит по этому пути.
   */
  async _loadLegacyModule() {
    if (this._legacyModulePath) {
      return import(/* @vite-ignore */ this._legacyModulePath);
    }
    return import('./cinematicRenderingWebGL2.js');
  }

  /**
   * Передача управления реализации v9.1.0 на WebGL2.
   * Чанк грузится лениво — там, где WebGPU есть, он не скачивается.
   */
  async _tryLegacyFallback(reason) {
    if (!this._allowLegacyFallback) {
      const r = Err(`WebGPU недоступен (${reason}), откат отключён настройкой`);
      this._resolveReady(r);
      return r;
    }

    try {
      log.info(`[Cinematic] Откат на WebGL2 (причина: ${reason})`);
      const legacyModule = await this._loadLegacyModule();
      const LegacyRenderer = legacyModule.CinematicRenderer;
      if (!LegacyRenderer) throw new Error('В legacy-модуле нет экспорта CinematicRenderer');

      // Убираем свой canvas, чтобы не перекрывал легаси-слой
      if (this._canvas?.parentNode) this._canvas.parentNode.removeChild(this._canvas);
      this._canvas = null;

      this._legacyRenderer = new LegacyRenderer(this._viewport, {
        // [10.19.12] Разрешение кадра переносится в откат ЧЕРЕЗ ОПЦИИ, а не
        // только вызовом setResolutionCap. Панель задаёт качество до enable(),
        // а откат создаётся уже внутри enable() — к тому моменту передавать
        // было некому, и на машине без WebGPU (то есть у врача) выбор качества
        // снова оказывался пустым. Поймано приёмкой: кадр выходил 344×416,
        // ровно по размеру окна, при выбранных 1024.
        ...this._options, preset: this._preset, autoStart: false,
        resolutionCap: this._resolutionCap,
      });
      const enableResult = this._legacyRenderer.enable();
      if (!enableResult.success) throw new Error(enableResult.error);

      // Переносим накопленные настройки
      if (Object.keys(this._customParams).length) {
        this._legacyRenderer.setParams(this._customParams, { trackHistory: false });
      }
      if (this._resolutionCap > 0 && this._legacyRenderer.setResolutionCap) {
        this._legacyRenderer.setResolutionCap(this._resolutionCap);
      }
      this._legacyRenderer.setSplitCompare(this._splitPos);
      // 10.19.18: карта глубины могла прийти ДО перехода на старый движок —
      // переносим её, иначе затенение осталось бы без данных, а признак глубины
      // врал бы в обратную сторону.
      if (this._depthSrc?.source) {
        const { source, ...прочее } = this._depthSrc;
        this._legacyRenderer.setDepthSource(source, прочее);
      }

      this._usingLegacy = true;
      events.emit(EVENTS.CINEMATIC_ENABLED, {
        preset: this._preset, backend: 'webgl2', version: this._version, fallback: true,
      });
      log.info('[Cinematic] Работаем на WebGL2 (сокращённый набор эффектов v9.1)');

      const r = Ok('webgl2');
      this._resolveReady(r);
      return r;
    } catch (e) {
      const r = Err(`Откат на WebGL2 не удался: ${e.message}`);
      log.error('[Cinematic]', r.error);
      this._resolveReady(r);
      return r;
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // РАЗРЕШЕНИЕ
  // ═══════════════════════════════════════════════════════════════

  _recalcResolution(csCanvas) {
    const source = csCanvas || this._viewport.canvas || this._viewport.element?.querySelector('canvas');
    if (!source || !this._canvas || !this._backend) return;

    let scale = 1.0;
    if (this._isInteracting) scale = 0.7;
    else if (this._useDRS) scale = this._perfMonitor.getScale();

    let базоваяШ = source.width;
    let базоваяВ = source.height;

    // [10.19.16] Выбранное качество задаёт длинную сторону кадра, но только
    // ВНИЗ от размера окна: см. пояснение ниже — надсэмплинг ломал картинку.
    // «Ультра 2048px» на окне 700px честно означает «700px»: панель об этом
    // говорит, вместо того чтобы показать чёрный кадр с красными точками.
    // [10.19.16] Ограничение разрешения может ТОЛЬКО УМЕНЬШАТЬ кадр.
    //
    // ПОВОД, и он мой. В 10.19.12 я сделал «Ультра 2048px» надсэмплингом: кадр
    // считался КРУПНЕЕ окна. Числа в приёмке сходились (кадр 846×1024, счётчик
    // кадров растёт), а картинка на машине врача превратилась в чёрное поле с
    // красными точками. Измерено на живой сборке: при кадре 846×1024 на окне
    // 344×416 непустых пикселей 3,9% и средняя яркость 1,2 — против 35% и 26 у
    // исходного окна; со снятым ограничением всё возвращается к норме.
    //
    // Причина в устройстве конвейера: сцена и глубина приходят текстурами
    // РАЗМЕРА ОКНА, а промежуточные буферы, радиусы SSAO и размытия считаются от
    // размера кадра. Уменьшение работало и до меня (динамическое понижение
    // качества давно живёт в _perfMonitor), увеличение не поддерживалось никогда.
    // Пока надсэмплинг не сделан и не проверен пиксельно — потолок равен окну.
    if (this._resolutionCap > 0) {
      const длинная = Math.max(базоваяШ, базоваяВ);
      const потолок = Math.min(this._resolutionCap, длинная);
      if (длинная > 0) {
        const k = потолок / длинная;
        базоваяШ = базоваяШ * k;
        базоваяВ = базоваяВ * k;
      }
    }

    const newW = Math.max(1, Math.floor(базоваяШ * scale));
    const newH = Math.max(1, Math.floor(базоваяВ * scale));

    if (this._targetW !== newW || this._targetH !== newH) {
      this._targetW = newW;
      this._targetH = newH;
      this._canvas.width = newW;
      this._canvas.height = newH;
      this._historyValid = false;
      this.forceRender();
    }

    // Разрешение рендера отличается от вывода, если включён апскейл:
    // сцена считается мельче, а до целевого размера её поднимает FSR
    const upscaleActive = this._params.upscale?.enabled && this._upscaler?.isActive();
    if (upscaleActive) {
      const res = this._upscaler.getRenderResolution(this._targetW, this._targetH);
      this._renderW = res.width;
      this._renderH = res.height;
    } else {
      this._renderW = this._targetW;
      this._renderH = this._targetH;
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // УПРАВЛЕНИЕ ПРЕСЕТАМИ
  // ═══════════════════════════════════════════════════════════════

  setPreset(id) {
    if (this._disposed) return Err('Renderer disposed');
    const builtin = CINEMATIC_PRESETS[id];
    const custom = this._loadCustomPresets()[id];
    const preset = builtin || custom;
    if (!preset) { log.warn(`[Cinematic] Неизвестный пресет: ${id}`); return Err(`Unknown preset: ${id}`); }
    if (!_validatePreset(preset)) return Err(`Invalid preset structure: ${id}`);

    const newParams = _deepClone(preset);
    const previousPreset = this._preset;
    const previousParams = this._params;

    try {
      this._preset = id;
      this._params = newParams;
      this._customParams = {};
      this._history.push(newParams, `setPreset:${id}`);
      this._applyEffectSettings();
      this._historyValid = false;

      if (this._usingLegacy && this._legacyRenderer) this._legacyRenderer.setPreset(id);

      this.forceRender();
      events.emit(EVENTS.CINEMATIC_PRESET_CHANGED, { preset: id, previous: previousPreset });
      log.debug(`[Cinematic] Пресет применён: ${id}`);
      return Ok(true);
    } catch (e) {
      this._preset = previousPreset;
      this._params = previousParams;
      return Err(e.message);
    }
  }

  setParams(params, options = {}) {
    if (this._disposed) return Err('Renderer disposed');
    if (!params || typeof params !== 'object') return Err('Params must be an object');

    const trackHistory = options.trackHistory !== false;
    try {
      const newParams = _deepMerge(this._params, params);
      if (trackHistory) this._history.push(this._params, 'setParams');
      this._params = newParams;
      this._customParams = _deepMerge(this._customParams, params);
      this._applyEffectSettings();

      if (this._usingLegacy && this._legacyRenderer) this._legacyRenderer.setParams(params, options);

      this.forceRender();
      events.emit(EVENTS.CINEMATIC_PARAMS_CHANGED, { changed: Object.keys(params), preset: this._preset });
      return Ok(true);
    } catch (e) { return Err(e.message); }
  }

  /** Прокинуть параметры пресета в AI-модули, у которых своё состояние. */
  _applyEffectSettings() {
    const p = this._params;
    if (this._denoiser && p.denoise) {
      if (p.denoise.preset) this._denoiser.setPreset(p.denoise.preset);
      if (typeof p.denoise.strength === 'number') {
        this._denoiser.setParams({ strength: p.denoise.enabled === false ? 0 : p.denoise.strength });
      }
    }
    if (this._upscaler && p.upscale) {
      if (p.upscale.quality) this._upscaler.setQuality(p.upscale.quality);
      if (p.upscale.mode) this._upscaler.setMode(p.upscale.enabled === false ? 'off' : p.upscale.mode);
      if (typeof p.upscale.sharpness === 'number') this._upscaler.setSharpness(p.upscale.sharpness);
      this._recalcResolution();
    }
  }

  getParams() { return _deepClone(this._params); }
  getCurrentPreset() { return this._preset; }

  undo() {
    if (this._disposed) return Err('Renderer disposed');
    const result = this._history.undo(this._params);
    if (!result.success) return result;
    this._params = result.value.state;
    this._applyEffectSettings();
    this.forceRender();
    events.emit(EVENTS.CINEMATIC_UNDO, { label: result.value.label });
    return Ok(true);
  }

  redo() {
    if (this._disposed) return Err('Renderer disposed');
    const result = this._history.redo(this._params);
    if (!result.success) return result;
    this._params = result.value.state;
    this._applyEffectSettings();
    this.forceRender();
    events.emit(EVENTS.CINEMATIC_REDO, { label: result.value.label });
    return Ok(true);
  }

  // ═══════════════════════════════════════════════════════════════
  // УПРАВЛЕНИЕ РЕНДЕРОМ
  // ═══════════════════════════════════════════════════════════════

  forceRender() {
    if (this._disposed) return;
    this._needsRender = true;
    if (this._usingLegacy && this._legacyRenderer) this._legacyRenderer.forceRender();
  }

  setInteractionState(isInteracting) {
    if (this._isInteracting === isInteracting) return;
    this._isInteracting = isInteracting;
    if (this._usingLegacy && this._legacyRenderer) {
      this._legacyRenderer.setInteractionState(isInteracting);
      return;
    }
    this._recalcResolution();
    this.forceRender();
  }

  setDepthSource(source, options = {}) {
    // [10.19.18] Источник глубины запоминается ЗДЕСЬ ВСЕГДА, а не только в
    // основном пути.
    //
    // ПОВОД. Карта приходит асинхронно (её надо посчитать), а переход на старый
    // движок WebGL2 происходит своим ходом при неудаче с WebGPU. Если карта
    // пришла до перехода, она уходила в никуда: старый рендерер её не получал, а
    // у обёртки признак глубины не выставлялся. Наружу это выглядело так:
    // hasDepth() === false при живой карте на руках, SSAO не запускается,
    // и в приёмке — «рендерер считает глубину настоящей: FAIL» при Жаккаре 0,965.
    this._depthSrc = source ? { source, ...options } : null;
    this._hasDepth = !!source;
    this._historyValid = false;
    if (this._usingLegacy && this._legacyRenderer) {
      this._legacyRenderer.setDepthSource(source, options);
      return;
    }
    this.forceRender();
  }

  setSplitCompare(pos) {
    if (typeof pos !== 'number' || isNaN(pos)) return Err('Position must be a number');
    this._splitPos = Math.max(0, Math.min(0.99, pos));
    if (this._usingLegacy && this._legacyRenderer) return this._legacyRenderer.setSplitCompare(pos);
    this.forceRender();
    return Ok(true);
  }

  getSplitCompare() { return this._splitPos; }

  // ═══════════════════════════════════════════════════════════════
  // КАМЕРА И СВЕТ (v10)
  // ═══════════════════════════════════════════════════════════════

  /**
   * Передать матрицы камеры. Без них не работают velocity-буфер (а значит
   * motion blur и темпоральные эффекты), объёмное освещение и NeRF.
   *
   * @param {Float32Array|number[]} viewProj матрица вида-проекции 4×4, column-major
   * @param {number[]} cameraPos позиция камеры в мире [x, y, z]
   */
  setCameraMatrices(viewProj, cameraPos) {
    if (!viewProj || viewProj.length !== 16) return Err('viewProj must be a 16-element matrix');
    if (!cameraPos || cameraPos.length < 3) return Err('cameraPos must be [x, y, z]');

    this._prevViewProj = this._viewProj;
    this._viewProj = new Float32Array(viewProj);
    this._cameraPos = [cameraPos[0], cameraPos[1], cameraPos[2]];
    this._hasMatrices = true;

    if (!this._prevViewProj) this._prevViewProj = new Float32Array(viewProj);

    this.forceRender();
    return Ok(true);
  }

  /** Собрать viewProj из привычных параметров камеры. */
  setCameraLookAt({ eye, target, up = [0, 1, 0], fovDeg = 45, near = 0.1, far = 100 }) {
    const aspect = this._targetH > 0 ? this._targetW / this._targetH : 1;
    const proj = mat4.perspective(fovDeg * Math.PI / 180, aspect, near, far);
    const view = mat4.lookAt(eye, target, up);
    return this.setCameraMatrices(mat4.multiply(proj, view), eye);
  }

  hasCameraMatrices() { return this._hasMatrices; }

  /**
   * Источники света для объёмного освещения. Больше четырёх не учитывается —
   * ограничение шейдера, каждый источник умножает стоимость ray marching.
   */
  setLights(lights) {
    if (!Array.isArray(lights)) return Err('lights must be an array');
    this._lights = lights.slice(0, 4).map(l => ({
      position: l.position || [0, 0, 0],
      intensity: l.intensity ?? 1.0,
      color: l.color || [1, 1, 1],
      radius: l.radius ?? 10,
    }));
    this.forceRender();
    return Ok(this._lights.length);
  }

  getLights() { return _deepClone(this._lights); }

  // ═══════════════════════════════════════════════════════════════
  // AI-МАСКА
  // ═══════════════════════════════════════════════════════════════

  setAIMask(maskData, dims, color = [1, 0, 0], opacity = 0.3) {
    if (this._usingLegacy && this._legacyRenderer) {
      return this._legacyRenderer.setAIMask(maskData, dims, color, opacity);
    }
    if (!this._backend?.isReady()) return Err('GPU не инициализирован');

    if (!maskData || opacity <= 0) {
      this._aiMaskOpacity = 0;
      this._hasAIMask = false;
      this.forceRender();
      return Ok(true);
    }

    try {
      this._backend.uploadData('aiMask', maskData, dims[0], dims[1], 'r8unorm');
      this._aiMaskColor = color;
      this._aiMaskOpacity = opacity;
      this._hasAIMask = true;
      this.forceRender();
      log.debug(`[Cinematic] AI-маска: ${dims[0]}×${dims[1]}, прозрачность ${opacity}`);
      return Ok(true);
    } catch (e) {
      return Err(e.message);
    }
  }

  clearAIMask() { return this.setAIMask(null, [0, 0], [1, 0, 0], 0); }

  setTemporalSSAOEnabled(enabled) {
    this._enableTemporalSSAO = !!enabled;
    if (this._usingLegacy && this._legacyRenderer) {
      return this._legacyRenderer.setTemporalSSAOEnabled(enabled);
    }
    this.forceRender();
    return Ok(true);
  }

  isTemporalSSAOEnabled() { return this._enableTemporalSSAO; }

  // ═══════════════════════════════════════════════════════════════
  // ПРОИЗВОДИТЕЛЬНОСТЬ
  // ═══════════════════════════════════════════════════════════════

  getFPS() {
    if (this._usingLegacy && this._legacyRenderer) return this._legacyRenderer.getFPS();
    return this._perfMonitor.getMetrics().avgFPS;
  }

  getMetrics() {
    if (this._usingLegacy && this._legacyRenderer) return this._legacyRenderer.getMetrics();
    return {
      ...this._perfMonitor.getMetrics(),
      backend: 'webgpu',
      version: this._version,
      renderResolution: [this._renderW, this._renderH],
      outputResolution: [this._targetW, this._targetH],
      upscale: this._upscaler?.getInfo() || null,
      denoiseMs: this._denoiser?.getCost() || 0,
    };
  }

  setTargetFPS(fps) { return this._perfMonitor.setTargetFPS(fps); }

  setDRSEnabled(enabled) {
    this._useDRS = !!enabled;
    if (!enabled) { this._perfMonitor.reset(); this._recalcResolution(); }
    if (this._usingLegacy && this._legacyRenderer) return this._legacyRenderer.setDRSEnabled(enabled);
    return Ok(true);
  }

  /**
   * [10.19.12] Разрешение кадра: длинная сторона в пикселях. 0 — «по размеру окна».
   *
   * Это и есть «Качество» из панели. Пересчёт делается сразу, поэтому смена
   * качества видна на следующем кадре, а не после изменения размера окна.
   * Проверить снаружи можно так: getMetrics().outputResolution — длинная сторона
   * обязана совпасть с заданной.
   */
  setResolutionCap(пиксели) {
    const n = Number(пиксели);
    if (пиксели !== 0 && (!Number.isFinite(n) || n < 0)) return Err('разрешение должно быть неотрицательным числом');
    this._resolutionCap = n > 0 ? Math.floor(n) : 0;
    // Обнуляем запомненный размер: иначе _recalcResolution решит, что менять
    // нечего, и новое качество вступит в силу только при изменении окна.
    this._targetW = 0;
    this._targetH = 0;
    this._recalcResolution();
    if (this._usingLegacy && this._legacyRenderer?.setResolutionCap) {
      this._legacyRenderer.setResolutionCap(this._resolutionCap);
    }
    this.forceRender();
    return Ok(this._resolutionCap);
  }

  getResolutionCap() { return this._resolutionCap || 0; }

  /**
   * [10.19.16] Собственный холст рендерера — наружу, чтобы кадр можно было
   * ЗАМЕРИТЬ, а не разглядывать. Повод: приёмка проверяла размер кадра и
   * счётчик кадров, всё сходилось, а на экране был почти чёрный кадр с красными
   * точками. Числа о том, что кадр СОБРАН, ничего не говорят о том, что в нём
   * что-то есть.
   */
  getCanvas() {
    if (this._usingLegacy && this._legacyRenderer?.getCanvas) return this._legacyRenderer.getCanvas();
    return this._canvas || null;
  }

  /**
   * [10.19.17] ЗАМЕР КАДРА СРЕДСТВАМИ GPU — второй, независимый от 2D-канваса
   * путь. Нужен потому, что снимок WebGPU-холста через drawImage может вернуть
   * пустоту, и тогда «кадр чёрный» неотличимо от «замерить не удалось».
   *
   * @returns {Promise<{ок: boolean, причина?: string, доляНепустых?: number,
   *   яркость?: number, яркостьСодержимого?: number, rgb?: number[],
   *   размер?: string, источник: string}>}
   */
  async замерКадраGPU(ждатьМс = 2000) {
    if (this._usingLegacy) {
      if (!this._legacyRenderer?.замерКадраGPU) return { ок: false, причина: 'старый рендерер не умеет замер с GPU', источник: 'webgl2' };
      return this._legacyRenderer.замерКадраGPU(ждатьМс);
    }
    const backend = this._backend;
    if (!backend?.запроситьЗамерХолста) return { ок: false, причина: 'бэкенд не умеет замер с GPU', источник: 'webgpu' };
    if (!this._enabled) return { ок: false, причина: 'рендерер выключен', источник: 'webgpu' };

    backend.запроситьЗамерХолста();
    this._needsRender = true;

    // Ждём кадр, в котором копия действительно поставлена.
    const t0 = Date.now();
    let сырое = null;
    while (Date.now() - t0 < ждатьМс) {
      await new Promise((r) => setTimeout(r, 60));
      сырое = await backend.прочитатьЗамерХолста();
      if (сырое) break;
    }
    if (!сырое) return { ок: false, причина: `копия холста не снята за ${ждатьМс} мс`, источник: 'webgpu' };
    return { ...статистикаПоБайтам(сырое), источник: 'webgpu' };
  }

  /**
   * [10.19.17] Есть ли НАСТОЯЩИЙ буфер глубины. Наружу — чтобы интерфейс не
   * предлагал затенение, которого не будет, и не подставлял вместо глубины
   * яркость картинки, как это делалось до 10.19.17.
   */
  /**
   * [10.19.19] ПЕРЕЙТИ НА WebGL2 ПО ТРЕБОВАНИЮ.
   *
   * ПОВОД. На машине Андрея Вячеславовича (RTX 4060) путь WebGPU кадра не даёт,
   * а WebGL2 работает — это замерено у меня на том же коде. Пока причина WebGPU
   * не найдена, у врача должен быть рычаг, а не ожидание следующей сборки. Откат
   * тут уже написан (он же срабатывает автоматически, когда WebGPU не поднялся);
   * наружу его не было выведено.
   *
   * @param {string} причина попадёт в журнал — чтобы потом было видно, ручной это
   *   переход или автоматический
   */
  async перейтиНаWebGL2(причина = 'по требованию') {
    if (this._disposed) return Err('Renderer disposed');
    if (this._usingLegacy) return Ok('webgl2');
    const былВключён = this._enabled;
    try { this._backend?.destroy(); } catch (e) { log.debug('[Cinematic]', e.message); }
    this._backend = null;
    this._gpuReady = false;
    const r = await this._tryLegacyFallback(причина);
    if (r.success) {
      this._enabled = былВключён || this._enabled;
      log.info('[Cinematic] Переключено на WebGL2 вручную');
    }
    return r;
  }

  /**
   * [10.19.19] Почему цикл не рисует. Наружу — чтобы плашка называла причину, а
   * не «кадр не получен за 8 с».
   * @returns {{причина: string, кадров: number, ошибка: string, движок: string}}
   */
  причинаПростоя() {
    if (this._usingLegacy && this._legacyRenderer?.причинаПростоя) {
      return { ...this._legacyRenderer.причинаПростоя(), движок: 'webgl2' };
    }
    return { ...(this._простой || { причина: '', кадров: 0, ошибка: '' }), движок: 'webgpu' };
  }

  hasDepth() {
    if (this._usingLegacy) return !!this._legacyRenderer?.hasDepth?.();
    return !!this._hasDepth;
  }

  isEnabled() { return this._enabled; }
  isDisposed() { return this._disposed; }
  isUsingWebGPU() { return !this._usingLegacy && this._gpuReady; }
  getBackendName() { return this._usingLegacy ? 'webgl2' : 'webgpu'; }
  getVersion() { return this._version; }

  getGPUInfo() {
    if (!this._backend) return null;
    return {
      adapter: this._backend.adapterInfo || null,
      features: [...this._backend.features],
      limits: this._backend.limits ? {
        maxTextureDimension2D: this._backend.limits.maxTextureDimension2D,
        maxComputeWorkgroupSizeX: this._backend.limits.maxComputeWorkgroupSizeX,
        maxStorageBufferBindingSize: this._backend.limits.maxStorageBufferBindingSize,
      } : null,
      stats: this._backend.getStats(),
      // Рантайм ONNX подтягивается лениво — до первой загрузки модели
      // здесь будет false, и это нормальное состояние, а не ошибка
      onnx: { loaded: isOrtLoaded(), backend: getOrtBackend() },
    };
  }

  // ═══════════════════════════════════════════════════════════════
  // AI-ПОДСИСТЕМЫ (v10)
  // ═══════════════════════════════════════════════════════════════

  /** Автоподбор параметров под содержимое кадра. */
  setAutoPresetEnabled(enabled) {
    this._autoPresetEnabled = !!enabled;
    this._presetOptimizer?.setEnabled(enabled);
    log.info(`[Cinematic] Автоподбор пресетов: ${enabled ? 'включён' : 'выключен'}`);
    return Ok(true);
  }

  isAutoPresetEnabled() { return this._autoPresetEnabled; }
  getSceneFeatures() { return this._sceneAnalyzer?.getFeatures() || null; }
  getPresetOptimizer() { return this._presetOptimizer; }
  getDenoiser() { return this._denoiser; }
  getUpscaler() { return this._upscaler; }
  getNeRFRenderer() { return this._nerf; }
  getVolumeRenderer() { return this._volume; }

  async loadPresetModel(path) {
    if (!this._presetOptimizer) return Err('GPU ещё не инициализирован');
    return this._presetOptimizer.loadModel(path);
  }

  async loadDenoiseModel(path) {
    if (!this._denoiser) return Err('GPU ещё не инициализирован');
    return this._denoiser.loadNeuralModel(path);
  }

  setUpscaleQuality(quality) {
    if (!this._upscaler) return Err('GPU ещё не инициализирован');
    const r = this._upscaler.setQuality(quality);
    if (r.success) { this._recalcResolution(); this.forceRender(); }
    return r;
  }

  setDenoisePreset(preset) {
    if (!this._denoiser) return Err('GPU ещё не инициализирован');
    const r = this._denoiser.setPreset(preset);
    if (r.success) this.forceRender();
    return r;
  }

  /**
   * Записать текущие настройки как обучающий пример: признаки сцены плюс
   * параметры, которые выбрал пользователь. Из этих пар и обучается модель.
   */
  recordPresetChoice(meta = {}) {
    const features = this._sceneAnalyzer?.getSmoothedFeatures();
    if (!features) return Err('Признаки сцены ещё не собраны');
    return this._presetOptimizer.collectTrainingSample(features, this._params, {
      preset: this._preset, ...meta,
    });
  }

  exportTrainingData() { return this._presetOptimizer?.exportTrainingData() || null; }

  // ═══════════════════════════════════════════════════════════════
  // ПОЛЬЗОВАТЕЛЬСКИЕ ПРЕСЕТЫ
  // ═══════════════════════════════════════════════════════════════

  saveCustomPreset(name) {
    if (this._disposed) return Err('Renderer disposed');
    if (!name || typeof name !== 'string' || name.trim().length < 2) {
      return Err('Preset name must be at least 2 characters');
    }
    try {
      const presets = this._loadCustomPresets();
      const id = 'custom_' + name.toLowerCase()
        .replace(/[^a-z0-9Ѐ-ӿ]+/g, '_')
        .replace(/^_|_$/g, '');
      if (CINEMATIC_PRESETS[id]) return Err(`Preset ID "${id}" conflicts with builtin preset`);

      presets[id] = {
        ..._deepClone(this._params),
        name: name.trim(),
        description: 'Пользовательский',
        createdAt: new Date().toISOString(),
        engineVersion: this._version,
      };

      const saveResult = set(this._storageKey, presets);
      if (!saveResult.success) {
        log.error('[Cinematic] Не удалось сохранить пресеты:', saveResult.error);
        return Err(`Save failed: ${saveResult.error}`);
      }

      // Выбор пользователя — самый ценный обучающий сигнал
      if (this._autoPresetEnabled) this.recordPresetChoice({ savedAs: id });

      events.emit(EVENTS.CINEMATIC_PRESET_SAVED, { id, name });
      log.info(`[Cinematic] Пресет сохранён: ${id}`);
      return Ok(id);
    } catch (e) {
      log.error('[Cinematic] Сохранение пресета не удалось:', e);
      return Err(e.message);
    }
  }

  deleteCustomPreset(id) {
    if (this._disposed) return Err('Renderer disposed');
    if (!id || !id.startsWith('custom_')) return Err('Can only delete custom presets');
    try {
      const presets = this._loadCustomPresets();
      if (!presets[id]) return Err(`Preset not found: ${id}`);
      delete presets[id];

      const saveResult = set(this._storageKey, presets);
      if (!saveResult.success) return Err(`Save failed: ${saveResult.error}`);
      if (this._preset === id) this.setPreset('cinematic');

      events.emit(EVENTS.CINEMATIC_PRESET_DELETED, { id });
      log.info(`[Cinematic] Пресет удалён: ${id}`);
      return Ok(true);
    } catch (e) { return Err(e.message); }
  }

  getAllPresets() { return { ...CINEMATIC_PRESETS, ...this._loadCustomPresets() }; }

  _loadCustomPresets() {
    try {
      const result = get(this._storageKey, {});
      if (result.success && result.value && typeof result.value === 'object') return result.value;
      if (!result.success) log.warn('[Cinematic] Не удалось загрузить пресеты:', result.error);
      return {};
    } catch (e) {
      log.warn('[Cinematic] Исключение при загрузке пресетов:', e);
      return {};
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // ЗАХВАТ МЕДИА
  // ═══════════════════════════════════════════════════════════════

  async captureScreenshot(opts = {}) {
    if (this._disposed) return Err('Renderer disposed');
    if (this._usingLegacy && this._legacyRenderer) return this._legacyRenderer.captureScreenshot(opts);
    if (!this._canvas) return Err('No canvas available');

    const { watermark = '', patientName = '', studyDate = '', format = 'image/png', quality = 0.95 } = opts;

    return new Promise((resolve) => {
      const renderCallback = () => {
        try {
          const w = this._canvas.width;
          const h = this._canvas.height;
          if (w === 0 || h === 0) { resolve(Err('Canvas has zero dimensions')); return; }

          const tmp = document.createElement('canvas');
          tmp.width = w; tmp.height = h;
          const ctx = tmp.getContext('2d');
          if (!ctx) { resolve(Err('Failed to create 2D context')); return; }

          ctx.drawImage(this._canvas, 0, 0);

          if (watermark || patientName || studyDate) {
            ctx.save();
            ctx.font = `bold ${Math.max(12, h * 0.02)}px sans-serif`;
            ctx.fillStyle = 'rgba(255,255,255,0.7)';
            ctx.shadowColor = 'rgba(0,0,0,0.8)';
            ctx.shadowBlur = 4;
            let y = h - 12;
            if (studyDate) { ctx.fillText(studyDate, 12, y); y -= Math.max(16, h * 0.025); }
            if (patientName) { ctx.fillText(patientName, 12, y); y -= Math.max(16, h * 0.025); }
            if (watermark) {
              ctx.textAlign = 'right';
              ctx.font = `${Math.max(10, h * 0.015)}px sans-serif`;
              ctx.fillStyle = 'rgba(255,255,255,0.4)';
              ctx.fillText(watermark, w - 12, h - 12);
            }
            ctx.restore();
          }

          tmp.toBlob((blob) => {
            if (blob) {
              events.emit(EVENTS.CINEMATIC_SCREENSHOT, { format, size: blob.size });
              resolve(Ok(blob));
            } else { resolve(Err('Failed to create blob')); }
          }, format, quality);
        } catch (e) { resolve(Err(e.message)); }
      };

      this._renderQueue.push(renderCallback);
      this.forceRender();
    });
  }

  startRecording(opts = {}) {
    if (this._disposed) return Err('Renderer disposed');
    if (this._usingLegacy && this._legacyRenderer) return this._legacyRenderer.startRecording(opts);
    if (!this._canvas) return Err('No canvas available');
    if (this._mediaRecorder) return Err('Already recording');

    const { fps = 30, bitrate = 5000000 } = opts;
    try {
      if (typeof MediaRecorder === 'undefined') return Err('MediaRecorder not supported');

      const stream = this._canvas.captureStream(fps);
      const mimeTypes = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
      const mime = mimeTypes.find(m => MediaRecorder.isTypeSupported(m));
      if (!mime) return Err('No supported video MIME type found');

      this._recordedChunks = [];
      this._mediaRecorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: bitrate });
      this._mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) this._recordedChunks.push(e.data); };
      this._mediaRecorder.onerror = (e) => { log.error('[Cinematic] Ошибка записи:', e); };
      this._mediaRecorder.start(100);

      this.forceRender();
      events.emit(EVENTS.CINEMATIC_RECORDING_STARTED, { fps, bitrate, mime });
      log.info(`[Cinematic] Запись начата: ${fps}fps @ ${bitrate / 1000}kbps`);
      return Ok(true);
    } catch (e) {
      log.error('[Cinematic] Старт записи не удался:', e);
      return Err(e.message);
    }
  }

  isRecording() {
    if (this._usingLegacy && this._legacyRenderer) return this._legacyRenderer.isRecording();
    return !!this._mediaRecorder;
  }

  async stopRecording() {
    if (this._usingLegacy && this._legacyRenderer) return this._legacyRenderer.stopRecording();
    if (!this._mediaRecorder) return Err('Not recording');

    return new Promise((resolve) => {
      const recorder = this._mediaRecorder;
      recorder.onstop = () => {
        try {
          const blob = new Blob(this._recordedChunks, { type: recorder.mimeType });
          const duration = this._recordedChunks.length * 100;
          this._recordedChunks = [];
          this._mediaRecorder = null;
          events.emit(EVENTS.CINEMATIC_RECORDING_STOPPED, {
            size: blob.size, mime: recorder.mimeType, durationMs: duration,
          });
          log.info(`[Cinematic] Запись остановлена: ${(blob.size / 1024 / 1024).toFixed(2)}MB`);
          resolve(Ok(blob));
        } catch (e) { resolve(Err(e.message)); }
      };
      try { recorder.stop(); } catch (e) { resolve(Err(e.message)); }
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // СОБЫТИЯ
  // ═══════════════════════════════════════════════════════════════

  _addEventHandler(target, event, handler, options) {
    target.addEventListener(event, handler, options);
    if (!this._eventHandlers.has(target)) this._eventHandlers.set(target, []);
    this._eventHandlers.get(target).push({ event, handler, options });
  }

  _unbindEvents() {
    for (const [target, handlers] of this._eventHandlers.entries()) {
      for (const { event, handler, options } of handlers) {
        try { target.removeEventListener(event, handler, options); } catch { /* элемент уже удалён */ }
      }
    }
    this._eventHandlers.clear();
  }

  _bindEvents() {
    const el = this._viewport.element;
    if (!el) return;

    this._addEventHandler(el, 'cornerstoneimagerendered', () => this.forceRender());
    this._addEventHandler(el, 'cornerstonenewimage', () => {
      this._historyValid = false;   // новый снимок — история темпоральных эффектов невалидна
      this.forceRender();
    });

    this._addEventHandler(el, 'pointerdown', (e) => {
      if (this._splitPos < 0.01 || this._splitPos > 0.99 || !this._enabled) return;
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      if (Math.abs(x - this._splitPos) < 0.05) {
        this._isDraggingSplit = true;
        el.setPointerCapture(e.pointerId);
        e.stopPropagation();
        e.preventDefault();
      }
    });

    this._addEventHandler(el, 'pointermove', (e) => {
      if (!this._enabled) return;
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      if (!this._isDraggingSplit) {
        if (this._splitPos > 0.01 && this._splitPos < 0.99) {
          el.style.cursor = Math.abs(x - this._splitPos) < 0.05 ? 'col-resize' : '';
        }
        return;
      }
      this.setSplitCompare(x);
      e.stopPropagation();
      e.preventDefault();
    });

    this._addEventHandler(el, 'pointerup', (e) => {
      if (this._isDraggingSplit) {
        this._isDraggingSplit = false;
        try { el.releasePointerCapture(e.pointerId); } catch { /* указатель уже отпущен */ }
        el.style.cursor = '';
      }
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // ЦИКЛ РЕНДЕРА
  // ═══════════════════════════════════════════════════════════════

  _startLoop() {
    if (this._animFrame) cancelAnimationFrame(this._animFrame);

    const loop = () => {
      if (!this._enabled || this._disposed || this._isContextLost) return;

      // [10.19.19] ПОЧЕМУ КАДРА НЕТ — записывается тут же.
      //
      // ПОВОД: у Андрея Вячеславовича плашка говорила «Кадр не получен за 8 с» и
      // больше ничего. А причин, по которым цикл может ничего не нарисовать,
      // здесь ПЯТЬ, и все они молчали: нет холста окна, бэкенд не готов
      // (в том числе потеря устройства), кадр никто не запрашивал, размер холста
      // ещё не совпал с целевым, предыдущий кадр не закончился. Отказ без
      // названной причины — это то, против чего написан весь остальной код.
      const csCanvas = this._viewport.canvas || this._viewport.element?.querySelector('canvas');
      const отметить = (причина) => {
        if (this._простой.причина !== причина) { this._простой.причина = причина; this._простой.кадров = 0; }
        this._простой.кадров++;
      };
      if (!csCanvas) отметить('у объёмного окна нет холста');
      else if (!this._canvas) отметить('холст слоя не создан');
      else if (!this._backend?.isReady()) {
        отметить(this._backend?.isDeviceLost?.() ? 'устройство WebGPU потеряно' : 'движок WebGPU не готов');
      }
      if (csCanvas && this._canvas && this._backend?.isReady()) {
        const isDynamic = (this._params.grain > 0.001) ||
                          this.isRecording() ||
                          this._needsTemporalAccumulation();

        if (!this._needsRender && !isDynamic) отметить('кадр не запрашивался (сцена не менялась)');
        else if (this._canvas.width !== this._targetW) отметить(`размер холста ${this._canvas.width} не совпал с целевым ${this._targetW}`);
        else if (this._isRendering) отметить('предыдущий кадр ещё считается');

        if ((this._needsRender || isDynamic) &&
            this._canvas.width === this._targetW &&
            !this._isRendering) {
          this._isRendering = true;
          try {
            const renderStart = performance.now();
            this._canvas.style.display = '';
            this._renderFrame(csCanvas);
            this._needsRender = false;

            if (this._renderQueue.length > 0) {
              const callbacks = this._renderQueue.splice(0);
              callbacks.forEach(cb => { try { cb(); } catch (e) { log.error('[Cinematic] Ошибка колбэка:', e); } });
            }

            const renderTime = performance.now() - renderStart;
            this._простой = { причина: '', кадров: 0, ошибка: '' };
            const scaleResult = this._perfMonitor.recordFrame(renderTime, this._backend.getStats());
            if (scaleResult.scaleChanged && this._useDRS) this._recalcResolution(csCanvas);
          } catch (e) {
            log.error('[Cinematic] Ошибка в цикле рендера:', e);
            this._простой = { причина: 'исключение в цикле рендера: ' + (e?.message || e), кадров: 1, ошибка: String(e?.message || e) };
            this._needsRender = true;
          } finally {
            this._isRendering = false;
          }
        }
      }
      this._animFrame = requestAnimationFrame(loop);
    };

    this._animFrame = requestAnimationFrame(loop);
  }

  /** Темпоральные эффекты сходятся за несколько кадров — им нужен перерисов. */
  _needsTemporalAccumulation() {
    const p = this._params;
    if (!this._historyValid) return true;
    return (p.ssgi?.enabled && p.ssgi?.temporal) ||
           (p.ssao?.enabled && this._enableTemporalSSAO);
  }

  // ═══════════════════════════════════════════════════════════════
  // КОНВЕЙЕР РЕНДЕРА
  // ═══════════════════════════════════════════════════════════════

  _renderFrame(csCanvas) {
    const backend = this._backend;
    if (!backend?.isReady()) return;

    const p = this._params;
    const W = this._renderW;
    const H = this._renderH;
    const outW = this._targetW;
    const outH = this._targetH;
    if (W < 1 || H < 1) return;

    const quality = this._perfMonitor.getAdaptiveQuality();
    const frame = this._frameCounter++;
    // 10.19.17: ось цветокора по самому кадру (frameMeasure.js). Контраст должен
    // поворачиваться вокруг середины изображения, а не вокруг константы 0,5.
    if (!this._ось) this._ось = new ОсьКадра(15);
    const midGray = this._ось.обновить(csCanvas);
    const time = (performance.now() - this._startTime) * 0.001;
    const texel = [1 / W, 1 / H];

    if (!backend.beginFrame()) return;

    // ── 0. Захват сцены и вспомогательных источников ──
    const sceneSrc = backend.copyCanvasToTarget(csCanvas, 'sceneSrc', FORMATS.LDR);
    if (!sceneSrc) { backend.endFrame(); return; }

    const origTarget = (this._splitPos > 0.005 && this._splitPos < 0.995)
      ? backend.copyCanvasToTarget(csCanvas, 'origSrc', FORMATS.LDR)
      : sceneSrc;

    const depthTarget = this._uploadDepth(W, H) || sceneSrc;
    const hasDepth = this._hasDepth;
    // [10.19.21] Нормали из объёма. Для полусферного SSAO нужны ещё и матрицы
    // камеры (точки берутся в мире); для N·L в объёмном свете матрицы и так
    // обязательны — тот проход без них не запускается.
    const normalTarget = this._uploadNormals(W, H);
    const hasNormals = !!normalTarget;
    const aiMaskTarget = this._hasAIMask ? backend.getTarget('aiMask') : null;

    // Пустой таргет-заглушка для необязательных входов шейдеров
    const dummy = backend.ensureTarget('dummy', 1, 1, FORMATS.HDR);

    let current = sceneSrc;
    let inputIsLinear = false;

    // ── 1. Velocity: основа motion blur и темпоральной репроекции ──
    let velocityTarget = dummy;
    const wantsVelocity = this._hasMatrices &&
      (p.motionBlur?.enabled || p.ssgi?.temporal || this._enableTemporalSSAO);

    if (wantsVelocity) {
      velocityTarget = backend.ensureTarget('velocity', W, H, FORMATS.VELOCITY);
      backend.runRenderPass('velocity', {
        textures: { texScene: sceneSrc.view, texDepth: depthTarget.view },
        uniforms: {
          uInvViewProj: mat4.invert(this._viewProj),
          uPrevViewProj: this._prevViewProj || this._viewProj,
          uResolution: [W, H],
          uScale: 1.0,
          uMaxVelocity: 0.15,
          uHasDepth: hasDepth ? 1 : 0,
          uHasMatrices: 1,
        },
        output: velocityTarget,
        clear: true,
      });
    }
    const hasVelocity = wantsVelocity ? 1 : 0;

    // ── 2. SSAO → AO v2 → применение ──
    // 10.19.17: только при НАСТОЯЩЕМ буфере глубины. Раньше окклюзия считалась
    // по яркости цветного кадра и темнила слой в 3,5 раза (замер 15,7 → 4,4).
    if (p.ssao?.enabled && hasDepth) {
      const aoRaw = backend.ensureTarget('aoRaw', W, H, FORMATS.HDR);
      // [10.19.21] Полусфера строится в мире, поэтому ей нужны матрицы камеры.
      // Без них (или без карты нормалей) шейдер работает прежним дисковым путём —
      // uHasNormals это и говорит.
      const полусфера = hasNormals && this._hasMatrices;
      backend.runRenderPass('ssao', {
        textures: { texScene: current.view, texDepth: depthTarget.view, texNormal: (normalTarget || dummy).view },
        uniforms: {
          uViewProj: this._hasMatrices ? this._viewProj : mat4.identity(),
          uInvViewProj: this._hasMatrices ? mat4.invert(this._viewProj) : mat4.identity(),
          uResolution: [W, H],
          uRadius: p.ssao.radius ?? 8,
          uIntensity: p.ssao.intensity ?? 0.5,
          uBias: p.ssao.bias ?? 0.002,
          // Окно окклюзии в долях диапазона near…far. Значение по умолчанию
          // выбрано замером (см. tests/verify_10_19_18.mjs): шире — затенение
          // расплывается в ореол вокруг силуэта, уже — исчезает вовсе.
          // Окно окклюзии задаётся В МИЛЛИМЕТРАХ и переводится в доли
          // диапазона near…far по числам самой карты глубины. Так порог значит
          // «затеняем то, что ближе на 25 мм», а не «на 0,02 чего-то».
          uDepthWindow: this._окноОкклюзии(p),
          uSamples: Math.min(p.ssao.samples ?? 24, quality.ssaoSamples),
          uHasDepth: hasDepth ? 1 : 0,
          uFrame: frame,
          uHasNormals: полусфера ? 1 : 0,
          // Радиус полусферы — те же миллиметры, что у окна окклюзии: одно
          // физическое понятие, одна ручка в панели.
          uRadiusWorld: this._радиусОкклюзииММ(p),
        },
        output: aoRaw,
        clear: true,
      });

      const aoClean = backend.ensureTarget('aoClean', W, H, FORMATS.HDR);
      const prevAO = backend.ensureTarget('prevAO', W, H, FORMATS.HDR);
      const prevDepth = backend.ensureTarget('prevDepth', W, H, FORMATS.HDR);

      backend.runRenderPass('ssaoDenoise', {
        textures: {
          texScene: current.view,
          texDepth: depthTarget.view,
          texAO: aoRaw.view,
          texPrevAO: prevAO.view,
          texPrevDepth: prevDepth.view,
          texVelocity: velocityTarget.view,
        },
        uniforms: {
          uResolution: [W, H],
          uTexelSize: texel,
          uTemporalWeight: quality.temporalWeight,
          uDepthSigma: 20.0,
          uSpatialSigma: 1.5,
          uHasDepth: hasDepth ? 1 : 0,
          uEnableTemporal: (this._enableTemporalSSAO && this._historyValid) ? 1 : 0,
          uHasVelocity: hasVelocity,
          uRadius: quality.denoiseRadius + 1,
          uFrame: frame,
        },
        output: aoClean,
        clear: true,
      });

      const shaded = backend.ensureTarget('aoShaded', W, H, FORMATS.HDR);
      backend.runRenderPass('aoApply', {
        textures: { texScene: current.view, texAO: aoClean.view },
        uniforms: {
          uResolution: [W, H],
          uInputIsLinear: inputIsLinear ? 1 : 0,
          uAOStrength: 1.0,
        },
        output: shaded,
        clear: true,
      });

      current = shaded;
      inputIsLinear = true;

      // История для следующего кадра
      backend.copyTargetToTarget('aoClean', 'prevAO');
      if (hasDepth) backend.copyTargetToTarget(depthTarget.name, 'prevDepth');
    }

    // ── 3. SSGI ──
    if (p.ssgi?.enabled && p.ssgi.intensity > 0.001) {
      const giRaw = backend.ensureTarget('giRaw', W, H, FORMATS.HDR);
      backend.runRenderPass('ssgi', {
        textures: { texScene: current.view, texDepth: depthTarget.view },
        uniforms: {
          uResolution: [W, H],
          uTexelSize: texel,
          uIntensity: p.ssgi.intensity,
          uRadius: p.ssgi.radius ?? 24,
          uThickness: p.ssgi.thickness ?? 0.06,
          uSamples: Math.min(p.ssgi.samples ?? 16, quality.ssgiSamples),
          uBounces: p.ssgi.bounces ?? 2,
          uHasDepth: hasDepth ? 1 : 0,
          uFrame: frame,
        },
        output: giRaw,
        clear: true,
      });

      const prevGI = backend.ensureTarget('prevGI', W, H, FORMATS.HDR);
      const giResolved = backend.ensureTarget('giResolved', W, H, FORMATS.HDR);

      backend.runRenderPass('ssgiResolve', {
        textures: {
          texScene: current.view,
          texDepth: depthTarget.view,
          texGI: giRaw.view,
          texPrevGI: prevGI.view,
          texVelocity: velocityTarget.view,
        },
        uniforms: {
          uResolution: [W, H],
          uTexelSize: texel,
          uTemporalWeight: p.ssgi.temporal ? quality.temporalWeight : 0,
          uIntensity: 1.0,
          uHasVelocity: hasVelocity,
          uHasDepth: hasDepth ? 1 : 0,
          uEnableTemporal: (p.ssgi.temporal && this._historyValid) ? 1 : 0,
          uFrame: frame,
        },
        output: giResolved,
        clear: true,
      });

      backend.copyTargetToTarget('giRaw', 'prevGI');
      current = giResolved;
      inputIsLinear = true;
    }

    // ── 4. Subsurface scattering ──
    if (p.sss?.enabled && p.sss.strength > 0.001) {
      const sssTarget = backend.ensureTarget('sss', W, H, FORMATS.HDR);
      backend.runRenderPass('sss', {
        textures: { texScene: current.view, texDepth: depthTarget.view },
        uniforms: {
          uResolution: [W, H],
          uTexelSize: texel,
          uStrength: p.sss.strength,
          uRadius: p.sss.radius ?? 1.2,
          uFalloffR: p.sss.falloffR ?? 1.0,
          uFalloffG: p.sss.falloffG ?? 3.5,
          uFalloffB: p.sss.falloffB ?? 7.0,
          uThicknessScale: p.sss.thicknessScale ?? 2.0,
          uSamples: Math.min(p.sss.samples ?? 12, quality.sssSamples),
          uHasDepth: hasDepth ? 1 : 0,
          uFrame: frame,
        },
        output: sssTarget,
        clear: true,
      });
      current = sssTarget;
    }

    // ── 5. Объёмное освещение ──
    let volumetricTarget = dummy;
    let hasVolumetric = 0;
    if (p.volumetric?.enabled && p.volumetric.intensity > 0.001 && this._hasMatrices) {
      volumetricTarget = backend.ensureTarget('volumetric', W, H, FORMATS.HDR);
      const L = this._lights;
      const lightUniforms = {};
      for (let i = 0; i < 4; i++) {
        const l = L[i];
        lightUniforms[`uLight${i}`] = l ? [...l.position, l.intensity] : [0, 0, 0, 0];
        lightUniforms[`uLightColor${i}`] = l ? [...l.color, l.radius] : [0, 0, 0, 1];
      }

      backend.runRenderPass('volumetricLight', {
        textures: { texScene: current.view, texDepth: depthTarget.view, texNormal: (normalTarget || dummy).view },
        uniforms: {
          uInvViewProj: mat4.invert(this._viewProj),
          uViewProj: this._viewProj,
          ...lightUniforms,
          uCameraPos: [...this._cameraPos, 1],
          uResolution: [W, H],
          uIntensity: p.volumetric.intensity,
          uScattering: p.volumetric.scattering ?? 0.3,
          uAbsorption: p.volumetric.absorption ?? 0.05,
          uAnisotropy: p.volumetric.anisotropy ?? 0.6,
          uSteps: Math.min(p.volumetric.steps ?? 32, quality.volumetricSteps),
          uLightCount: Math.min(L.length, 4),
          uHasDepth: hasDepth ? 1 : 0,
          uShadowEnabled: p.volumetric.shadows ? 1 : 0,
          uMaxDistance: p.volumetric.maxDistance ?? 40,
          uFrame: frame,
          // [10.19.21] Нормаль включает N·L по поверхности: без неё источники
          // объёмного света рождали только туман, и поверхность не отзывалась.
          uHasNormals: hasNormals ? 1 : 0,
        },
        output: volumetricTarget,
        clear: true,
      });
      hasVolumetric = 1;
    }

    // ── 6. Bloom и god rays ──
    let bloomTarget = dummy;
    let godRaysTarget = dummy;

    if (p.bloom?.enabled || p.godRays?.enabled) {
      const bW = Math.max(1, W >> 1);
      const bH = Math.max(1, H >> 1);

      const bright = backend.ensureTarget('bright', bW, bH, FORMATS.HDR);
      backend.runRenderPass('bright', {
        textures: { texScene: current.view },
        uniforms: {
          uThreshold: p.bloom?.threshold ?? 0.6,
          uSoftKnee: p.bloom?.softKnee ?? 0.3,
          uInputIsLinear: inputIsLinear ? 1 : 0,
          uClampMax: 8.0,
        },
        output: bright,
        clear: true,
      });

      if (p.bloom?.enabled) {
        const blurA = backend.ensureTarget('blurA', bW, bH, FORMATS.HDR);
        const blurB = backend.ensureTarget('blurB', bW, bH, FORMATS.HDR);
        const passes = Math.min(p.bloom.passes ?? 5, quality.bloomPasses);

        let source = bright;
        for (let i = 0; i < passes; i++) {
          backend.runRenderPass('blur', {
            textures: { texSrc: source.view },
            uniforms: { uTexelSize: [1 / bW, 1 / bH], uDirection: [1.0 + i * 0.5, 0.0] },
            output: blurA,
            clear: true,
          });
          backend.runRenderPass('blur', {
            textures: { texSrc: blurA.view },
            uniforms: { uTexelSize: [1 / bW, 1 / bH], uDirection: [0.0, 1.0 + i * 0.5] },
            output: blurB,
            clear: true,
          });
          source = blurB;
        }
        bloomTarget = source;
      }

      if (p.godRays?.enabled) {
        godRaysTarget = backend.ensureTarget('godRays', bW, bH, FORMATS.HDR);
        backend.runRenderPass('godRays', {
          textures: { texBright: bright.view },
          uniforms: {
            uLightPos: [0.5, 0.5],
            uDensity: p.godRays.density ?? 0.8,
            uDecay: p.godRays.decay ?? 0.95,
            uWeight: p.godRays.weight ?? 0.3,
            uExposure: 1.0,
            uSamples: quality.godRaysSamples,
            uFrame: frame,
            uResolution: [bW, bH],
          },
          output: godRaysTarget,
          clear: true,
        });
      }
    }

    // ── 7. Сведение ──
    const cg = p.colorGrade || {};
    const tm = p.toneMap || {};
    const dof = p.dof || {};
    const ed = p.edge || {};

    const composite = backend.ensureTarget('composite', W, H, FORMATS.HDR);
    backend.runRenderPass('composite', {
      textures: {
        texScene: current.view,
        texBloom: bloomTarget.view,
        texOriginal: origTarget.view,
        texGodRays: godRaysTarget.view,
        texAIMask: aiMaskTarget?.view || dummy.view,
        texVolumetric: volumetricTarget.view,
      },
      uniforms: {
        uResolution: [W, H],
        uColorTint: cg.tint || [1, 1, 1],
        uEdgeColor: ed.color || [0.3, 0.6, 1],
        uAIMaskColor: this._aiMaskColor,
        uTime: time,
        uExposure: tm.exposure ?? 1,
        uGamma: tm.gamma ?? 2.2,
        uBloomIntensity: p.bloom?.enabled ? (p.bloom.intensity ?? 0.2) : 0,
        uGodRaysIntensity: p.godRays?.enabled ? (p.godRays.intensity ?? 0) : 0,
        uVolumetricIntensity: p.volumetric?.enabled ? 1.0 : 0,
        uSharpen: p.sharpen ?? 0,
        uVignetteIntensity: p.vignette?.intensity ?? 0,
        uVignetteRadius: p.vignette?.radius ?? 0.85,
        uSaturation: cg.saturation ?? 1,
        uContrast: cg.contrast ?? 1,
        uMidGray: midGray,
        uColorTemp: cg.temperature ?? 0,
        uDofIntensity: p.bokeh?.enabled ? 0 : (dof.intensity ?? 0),
        uDofFocus: dof.focus ?? 0.5,
        uDofRange: dof.range ?? 0.3,
        uEdgeIntensity: ed.intensity ?? 0,
        uChromAbIntensity: p.chromAb ?? 0,
        uGrainIntensity: p.grain ?? 0,
        uSplitPos: this._splitPos,
        uAIMaskOpacity: this._aiMaskOpacity,
        uToneMapMode: tm.mode ?? 0,
        uInputIsLinear: inputIsLinear ? 1 : 0,
        uHasVolumetric: hasVolumetric,
        uFrame: frame,
      },
      output: composite,
      clear: true,
    });

    current = composite;

    // ── 8. Цепочка пост-эффектов ──
    current = this._chainPass(current, 'ssr', W, H, depthTarget, hasDepth, quality, frame, p);
    current = this._chainPass(current, 'volFog', W, H, depthTarget, hasDepth, quality, frame, p, time);
    current = this._chainPass(current, 'colorLut', W, H, depthTarget, hasDepth, quality, frame, p);
    current = this._chainPass(current, 'bokehDof', W, H, depthTarget, hasDepth, quality, frame, p);

    // ── 9. Motion blur ──
    if (p.motionBlur?.enabled && p.motionBlur.intensity > 0.001 && hasVelocity) {
      const mb = backend.ensureTarget('motionBlur', W, H, FORMATS.HDR);
      backend.runRenderPass('motionBlur', {
        textures: {
          texScene: current.view,
          texDepth: depthTarget.view,
          texVelocity: velocityTarget.view,
        },
        uniforms: {
          uResolution: [W, H],
          uTexelSize: texel,
          uIntensity: p.motionBlur.intensity,
          uSamples: Math.min(p.motionBlur.samples ?? 12, quality.motionBlurSamples),
          uHasVelocity: 1,
          uHasDepth: hasDepth ? 1 : 0,
          uShutterAngle: p.motionBlur.shutterAngle ?? 0.5,
          uFrame: frame,
        },
        output: mb,
        clear: true,
      });
      current = mb;
    }

    // ── 10. AI-денойзинг (compute) ──
    if (p.denoise?.enabled !== false && this._denoiser) {
      const denoised = this._denoiser.denoise({
        srcView: current.view,
        depthView: depthTarget.view,
        dstName: 'denoised',
        width: W, height: H,
        hasDepth,
      });
      if (denoised) current = denoised;
    }

    // ── 11. Апскейл (FSR EASU + RCAS) ──
    if (p.upscale?.enabled && this._upscaler?.isActive()) {
      const upscaled = this._upscaler.upscale({
        srcView: current.view,
        srcWidth: W, srcHeight: H,
        targetWidth: outW, targetHeight: outH,
      });
      if (upscaled) current = upscaled;
    }

    // ── 12. Финал: FXAA или прямой blit в canvas ──
    const canvasView = backend.getCanvasView();
    if (canvasView) {
      if (p.fxaa?.enabled) {
        backend.runRenderPass('fxaa', {
          textures: { texSrc: current.view },
          uniforms: { uResolution: [outW, outH], uSpanMax: 8.0, uReduceMul: 1.0 / 128.0 },
          output: canvasView,
          outputFormat: backend.canvasFormat,
          clear: true,
        });
      } else {
        backend.runRenderPass('blit', {
          textures: { texSrc: current.view },
          uniforms: { uResolution: [outW, outH], uOpacity: 1.0, uUseNearest: 0 },
          output: canvasView,
          outputFormat: backend.canvasFormat,
          clear: true,
        });
      }
    }

    // ── 12б. Замер кадра прямо с GPU (если заказан) ──
    // Копия ставится в ТОТ ЖЕ командный буфер, сразу после записи холста, —
    // это единственный момент, когда текстуру холста можно честно прочитать.
    try { backend.снятьХолстВБуфер?.(); } catch (e) { log.debug('[Cinematic/замер]', e.message); }

    // ── 13. Анализ сцены для автоподбора ──
    if (this._autoPresetEnabled && this._sceneAnalyzer?.shouldAnalyze()) {
      this._sceneAnalyzer.recordPass({
        srcView: sceneSrc.view,
        depthView: depthTarget.view,
        width: W, height: H,
        hasDepth,
      });
    }

    backend.endFrame();
    this._historyValid = true;
    this._prevViewProj = this._viewProj;

    // Чтение статистики и автоподбор — вне кадра, асинхронно
    if (this._autoPresetEnabled) this._runAutoPreset();
  }

  /** Однородный запуск необязательных эффектов цепочки. */
  _chainPass(current, passName, W, H, depthTarget, hasDepth, quality, frame, p, time = 0) {
    const backend = this._backend;

    // 10.19.17: проходы, читающие глубину, без буфера глубины НЕ идут вообще.
    // Раньше «глубиной» им служила яркость цветного кадра — выдумка, из которой
    // рождались чёрные пятна там, где никакой геометрии нет.
    const нужнаГлубина = passName === 'ssr' || passName === 'volFog' || passName === 'bokehDof';
    if (нужнаГлубина && !hasDepth) return current;

    if (passName === 'ssr') {
      if (!p.ssr?.enabled || (p.ssr.intensity ?? 0) <= 0.001) return current;
      const t = backend.ensureTarget('ssrOut', W, H, FORMATS.HDR);
      backend.runRenderPass('ssr', {
        textures: { texScene: current.view, texDepth: depthTarget.view },
        uniforms: {
          uResolution: [W, H],
          uIntensity: p.ssr.intensity,
          uRoughness: p.ssr.roughness ?? 0.5,
          uMaxDist: p.ssr.maxDist ?? 30,
          uSteps: quality.ssrSteps,
          uHasDepth: hasDepth ? 1 : 0,
          uFrame: frame,
        },
        output: t, clear: true,
      });
      return t;
    }

    if (passName === 'volFog') {
      if (!p.fog?.enabled || (p.fog.density ?? 0) <= 0.001) return current;
      const t = backend.ensureTarget('fogOut', W, H, FORMATS.HDR);
      backend.runRenderPass('volFog', {
        textures: { texScene: current.view, texDepth: depthTarget.view },
        uniforms: {
          uResolution: [W, H],
          uFogColor: p.fog.color || [0.05, 0.08, 0.12],
          uDensity: p.fog.density,
          uFalloff: p.fog.falloff ?? 2.0,
          uHeight: p.fog.height ?? 0.3,
          uTime: time,
          uHasDepth: hasDepth ? 1 : 0,
        },
        output: t, clear: true,
      });
      return t;
    }

    if (passName === 'colorLut') {
      if (!(p.lut?.mode > 0) || (p.lut.intensity ?? 0) <= 0.001) return current;
      const t = backend.ensureTarget('lutOut', W, H, FORMATS.HDR);
      backend.runRenderPass('colorLut', {
        textures: { texScene: current.view },
        uniforms: { uLutMode: p.lut.mode, uIntensity: p.lut.intensity, uMidGray: this._ось ? this._ось.значение() : 0.18 },
        output: t, clear: true,
      });
      return t;
    }

    if (passName === 'bokehDof') {
      if (!p.bokeh?.enabled || (p.dof?.intensity ?? 0) <= 0.001) return current;
      const t = backend.ensureTarget('bokehOut', W, H, FORMATS.HDR);
      backend.runRenderPass('bokehDof', {
        textures: { texScene: current.view, texDepth: depthTarget.view },
        uniforms: {
          uResolution: [W, H],
          uIntensity: p.dof.intensity,
          uFocus: p.dof.focus ?? 0.5,
          uRange: p.dof.range ?? 0.3,
          uBokehSize: p.bokeh.size ?? 0.5,
          uBokehBright: p.bokeh.bright ?? 2.0,
          uRings: Math.min(p.bokeh.rings ?? 4, quality.bokehRings),
          uHasDepth: hasDepth ? 1 : 0,
          uFrame: frame,
        },
        output: t, clear: true,
      });
      return t;
    }

    return current;
  }

  /**
   * Окно окклюзии в долях диапазона глубины. [10.19.18]
   *
   * Карта глубины линейная и несёт свои near/far в миллиметрах, поэтому
   * физическое расстояние переводится в доли однозначно. Пока карты нет —
   * возвращается запасное значение, но и SSAO в этом случае не запускается.
   */
  _окноОкклюзии(p) {
    const мм = p?.ssao?.окноММ ?? 25;
    const near = this._depthSrc?.near, far = this._depthSrc?.far;
    if (typeof near === 'number' && typeof far === 'number' && far > near) {
      return Math.min(0.5, Math.max(0.0005, мм / (far - near)));
    }
    return p?.ssao?.depthWindow ?? 0.02;
  }

  /**
   * Радиус полусферы SSAO в МИЛЛИМЕТРАХ. [10.19.21]
   *
   * Нарочно то же число, что окно окклюзии: и радиус выборок, и допуск по
   * глубине — одно физическое понятие «на каком расстоянии соседняя геометрия
   * ещё затеняет». Две ручки для одного понятия разъехались бы у пользователя.
   */
  _радиусОкклюзииММ(p) {
    return p?.ssao?.окноММ ?? 25;
  }

  _uploadDepth(W, H) {
    if (!this._depthSrc?.source) return null;
    const { source, width, height, isFloat } = this._depthSrc;

    try {
      if (isFloat && source instanceof Float32Array) {
        return this._backend.uploadData('depth', source, width || W, height || H, 'r32float');
      }
      if (source instanceof HTMLCanvasElement || source instanceof ImageBitmap ||
          source instanceof HTMLImageElement || source instanceof OffscreenCanvas) {
        return this._backend.copyCanvasToTarget(source, 'depth', FORMATS.LDR);
      }
      if (source instanceof Uint8Array || source instanceof Uint8ClampedArray) {
        return this._backend.uploadData('depth', source, width || W, height || H, 'r8unorm');
      }
      return null;
    } catch (e) {
      log.warn('[Cinematic] Загрузка глубины не удалась:', e.message);
      return null;
    }
  }

  /**
   * Карта нормалей из объёма [10.19.21]: тот же источник, что глубина
   * (depthFromVolume.js), сырой RGBA8 — n·0,5+0,5 в rgb, годность в альфе.
   * Нет карты — возвращается null, и эффекты по нормалям честно не включаются:
   * полусферный SSAO откатывается на дисковый путь, N·L в объёмном свете гаснет.
   */
  _uploadNormals(W, H) {
    const н = this._depthSrc?.нормали;
    if (!(н instanceof Uint8Array) || !н.length) return null;
    const { width, height } = this._depthSrc;
    try {
      return this._backend.uploadData('normal', н, width || W, height || H, 'rgba8unorm');
    } catch (e) {
      log.warn('[Cinematic] Загрузка нормалей не удалась:', e.message);
      return null;
    }
  }

  async _runAutoPreset() {
    if (this._autoPresetRunning || !this._sceneAnalyzer || !this._presetOptimizer) return;
    this._autoPresetRunning = true;

    try {
      const features = await this._sceneAnalyzer.collect();
      if (!features || this._disposed) return;

      const smoothed = this._sceneAnalyzer.getSmoothedFeatures();
      const target = await this._presetOptimizer.optimize(smoothed || features);
      if (!target || this._disposed) return;

      // Плавный переход: резкая смена настроек выглядит как сбой рендера
      const blended = this._presetOptimizer.blend(this._params, target);
      this.setParams(blended, { trackHistory: false });

      events.emit(EVENTS.CINEMATIC_PARAMS_CHANGED, {
        changed: Object.keys(target), preset: this._preset, source: 'auto',
      });
    } catch (e) {
      log.warn('[Cinematic] Автоподбор не удался:', e.message);
    } finally {
      this._autoPresetRunning = false;
    }
  }
}

// ═══════════════════════════════════════════════════════════════════
// 6. ФАБРИКИ И ПУБЛИЧНЫЙ API
// ═══════════════════════════════════════════════════════════════════

export function createCinematicRenderer(viewport, options = {}) {
  try {
    return Ok(new CinematicRenderer(viewport, options));
  } catch (e) {
    return Err(e.message);
  }
}

/** Асинхронная фабрика: возвращает рендерер уже готовым к работе. */
export async function createCinematicRendererAsync(viewport, options = {}) {
  try {
    const renderer = new CinematicRenderer(viewport, { ...options, autoStart: false });
    const enableResult = renderer.enable();
    if (!enableResult.success) { renderer.dispose(); return enableResult; }

    const readyResult = await renderer.whenReady();
    if (!readyResult.success) { renderer.dispose(); return readyResult; }

    return Ok(renderer);
  } catch (e) {
    return Err(e.message);
  }
}

export function getCinematicPresetIds() { return Object.keys(CINEMATIC_PRESETS); }

export function getCinematicPresetById(id) {
  const preset = CINEMATIC_PRESETS[id];
  return preset ? { id, ..._deepClone(preset) } : null;
}

export function getCinematicPresetCount() { return Object.keys(CINEMATIC_PRESETS).length; }
export function getBuiltinPresetIds() { return Object.keys(CINEMATIC_PRESETS); }
export function getLutModes() { return LUT_MODES; }
export function getToneMapModes() { return TONE_MAP_MODES; }

export function getAvailablePresets() {
  return Object.entries(CINEMATIC_PRESETS).map(([id, p]) => ({
    id, name: p.name, description: p.description,
  }));
}

/** Поддерживается ли WebGPU. Полезно до создания рендерера. */
export function isWebGPUSupported() { return WebGPUBackend.isSupported(); }

/** Подробности о GPU без создания рендерера — для диагностики и телеметрии. */
export async function getGPUCapabilities() {
  if (!WebGPUBackend.isSupported()) {
    return { supported: false, reason: 'navigator.gpu недоступен' };
  }
  try {
    const adapter = await navigator.gpu.requestAdapter({ powerPreference: 'high-performance' });
    if (!adapter) return { supported: false, reason: 'Подходящий адаптер не найден' };

    let info = null;
    if (typeof adapter.requestAdapterInfo === 'function') {
      info = await adapter.requestAdapterInfo();
    } else if (adapter.info) {
      info = adapter.info;
    }

    return {
      supported: true,
      info,
      features: [...adapter.features],
      limits: {
        maxTextureDimension2D: adapter.limits.maxTextureDimension2D,
        maxComputeWorkgroupSizeX: adapter.limits.maxComputeWorkgroupSizeX,
        maxComputeInvocationsPerWorkgroup: adapter.limits.maxComputeInvocationsPerWorkgroup,
        maxStorageBufferBindingSize: adapter.limits.maxStorageBufferBindingSize,
      },
    };
  } catch (e) {
    return { supported: false, reason: e.message };
  }
}

export function createCinematicPanel() {
  log.warn('[Cinematic] createCinematicPanel() устарел, используйте createCinematicRenderer()');
  return null;
}

export { CINEMATIC_PRESETS as PRESETS };
export { DENOISE_PRESETS, UPSCALE_QUALITY, VOLUME_WINDOW_PRESETS, mat4 };
export { NeRFRenderer, VolumeRenderer, AIDenoiser, Upscaler, AIPresetOptimizer, SceneAnalyzer };
